// A React component for 8th Wall AFrame scenes. The scene HTML can be supplied, along with
// any components or primitives that should be registered, and any image targets that should be
// loaded if something other than the automatically loaded set is wanted. Passing
// DISABLE_IMAGE_TARGETS will prevent any image targets from loading, including ones that would
// otherwise enabled automatically.
declare let window: any
declare let AFRAME: any
declare let React: any
declare let XR8: any

// Helper function to make sure that aframe components are only registered once, since they can't
// be cleanly unregistered.
const registeredComponents = new Set<string>()
const registerComponents = (components: INamedObject[]) => {
  if (typeof AFRAME === 'undefined') {
    console.warn('⚠️ AFRAME non disponibile, componenti non registrati')
    return
  }
  components.forEach(({ name, val }: INamedObject) => {
    if (registeredComponents.has(name)) {
      return
    }
    registeredComponents.add(name)
    try {
      AFRAME.registerComponent(name, val)
    } catch (error) {
      console.error(`❌ Errore nella registrazione del componente "${name}":`, error)
    }
  })
}

// Helper function to make sure that aframe primitives are only registered once, since they can't
// be cleanly unregistered.
const registeredPrimitives = new Set<string>()
const registerPrimitives = (primitives: INamedObject[]) => {
  if (typeof AFRAME === 'undefined') {
    console.warn('⚠️ AFRAME non disponibile, primitive non registrate')
    return
  }
  primitives.forEach(({ name, val }: INamedObject) => {
    if (registeredPrimitives.has(name)) {
      return
    }
    registeredPrimitives.add(name)
    try {
      AFRAME.registerPrimitive(name, val)
    } catch (error) {
      console.error(`❌ Errore nella registrazione della primitiva "${name}":`, error)
    }
  })
}

interface INamedObject {
  name: string
  val: any
}

interface IAFrameSceneProps {
  sceneHtml: string
  imageTargets?: string[]
  components?: INamedObject[]
  primitives?: INamedObject[]
}



const uniqueStrings = (items: string[]) => Array.from(new Set((items || []).filter(Boolean)))

// Extract 8th Wall image target names from scene HTML:
//   <xrextras-named-image-target name="target-name">
const inferImageTargetsFromSceneHtml = (sceneHtml: string): string[] => {
  if (!sceneHtml) return []
  try {
    const targets: string[] = []
    const re = /<xrextras-named-image-target[^>]*\sname=["']([^"']+)["'][^>]*>/gi
    let m: RegExpExecArray | null
    while ((m = re.exec(sceneHtml)) !== null) {
      if (m[1]) targets.push(m[1])
    }
    return uniqueStrings(targets)
  } catch (_e) {
    return []
  }
}

const configureXR8ImageTargets = async (targets: string[], source: string, opts?: { disableWorldTracking?: boolean }) => {
  const imageTargetNames = uniqueStrings(targets)
  if (!imageTargetNames.length) return

  // Self-hosted: fetch dei file JSON dei target dal server locale
  const targetJsonArray = await Promise.all(
    imageTargetNames.map(async (name) => {
      try {
        const resp = await fetch(`/image-targets/${name}.json`)
        if (!resp.ok) {
          console.warn(`⚠️ Target JSON non trovato: /image-targets/${name}.json (${resp.status})`)
          return null
        }
        const data = await resp.json()
        // XR8 è caricato da apps.8thwall.com: non può risolvere path relativi alla nostra app.
        // Dobbiamo passare un URL assoluto completo.
        if (data.imagePath && !data.imagePath.startsWith('http')) {
          const slash = data.imagePath.startsWith('/') ? '' : '/'
          data.imagePath = `${window.location.origin}${slash}${data.imagePath}`
        }
        console.log(`📦 Target JSON caricato: ${name}`, { imagePath: data.imagePath, type: data.type })
        return data
      } catch (e) {
        console.warn(`⚠️ Errore fetch target JSON: ${name}`, e)
        return null
      }
    })
  )

  const validTargets = targetJsonArray.filter(Boolean)
  if (!validTargets.length) {
    console.error(`❌ Nessun target JSON valido trovato per: ${imageTargetNames.join(', ')}`)
    return
  }

  const disableWorldTracking = !!opts?.disableWorldTracking

  // Approccio ufficiale 8th Wall (studio-image-targets-example/src/app.js):
  // configure viene chiamato direttamente nell'handler xrloaded, NON tramite pipeline module.
  const doXrConfigure = () => {
    try {
      if (typeof XR8 === 'undefined' || !XR8 || !XR8.XrController) {
        console.warn(`⚠️ XR8 non disponibile [${source}]`)
        return
      }
      console.log(`🎯 XR8.XrController.configure [${source}]`, {
        targets: validTargets.map((t: any) => t.name),
        imagePaths: validTargets.map((t: any) => t.imagePath),
        disableWorldTracking,
      })
      XR8.XrController.configure({ imageTargetData: validTargets, disableWorldTracking })
    } catch (e) {
      console.error(`❌ Errore configure [${source}]`, e)
    }
  }

  // Identico a: window.XR8 ? onxrloaded() : window.addEventListener('xrloaded', onxrloaded)
  if (typeof XR8 !== 'undefined' && XR8) {
    doXrConfigure()
  } else {
    window.addEventListener('xrloaded', () => doXrConfigure(), { once: true } as any)
  }
}

// Intercettazione e manipulazione visiva basata su Image Targets 8th Wall
let activeXRTargets = new Set();

const hideScanElements = () => {
  const targetImageOverlay = document.getElementById('targetImageOverlay');
  if (targetImageOverlay) {
    targetImageOverlay.style.display = 'none';
  }

  const scanElements = document.querySelectorAll('.scan-figure, .figura-scan, [id*="scan"], [class*="scan"]');
  scanElements.forEach(element => {
    if (element && (element as HTMLElement).style) {
      (element as HTMLElement).style.display = 'none';
      (element as HTMLElement).style.visibility = 'hidden';
      (element as HTMLElement).style.opacity = '0';
    }
  });
};

const showScanElements = () => {
  const targetImageOverlay = document.getElementById('targetImageOverlay');
  if (targetImageOverlay) {
    targetImageOverlay.style.display = 'block';
  }

  const scanElements = document.querySelectorAll('.scan-figure, .figura-scan, [id*="scan"], [class*="scan"]');
  scanElements.forEach(element => {
    if (element && (element as HTMLElement).style) {
      (element as HTMLElement).style.display = 'block';
      (element as HTMLElement).style.visibility = 'visible';
      (element as HTMLElement).style.opacity = '1';
    }
  });
};

// Import della funzione di inizializzazione
// Ora che abbiamo reso tutto lazy, possiamo importare direttamente
import { initializeAframeComponents } from '../views/aframe-init'

// A react component for loading and unloading an aframe scene. The initial scene contents should
// be specified as an html string in sceneHtml. All props must be specified when the component
// mounts. Updates to props will be ignored.
//
// Optionally, aframe coponents to register for this scene can be passed as [{name, val}] arrays.
// Care is needed here to not define the same component different across scenes, since aframe
// components can't be unloaded.
//
// Optionally imageTargets can be specified to override the set loaded by default.
function AFrameScene({ sceneHtml, imageTargets, components, primitives }: IAFrameSceneProps) {
  React.useEffect(() => {
    const run = async () => {
      // Inizializza i componenti A-Frame globali se non sono ancora inizializzati
      initializeAframeComponents()

      // Ensure Draco support is installed BEFORE inserting the scene into the DOM.
      // This prevents the first Draco-compressed GLB from being parsed without a DRACOLoader.
      try {
        const w: any = window as any
        if (w && typeof w.__iscagEnsureDraco === 'function') {
          console.log('🧩 Draco: attendo __iscagEnsureDraco() prima di montare la scena…')
          await Promise.race([
            w.__iscagEnsureDraco(),
            new Promise((resolve) => setTimeout(resolve, 5000)),
          ])
        }
      } catch (_e) { }

      // Image targets:
      // - if imageTargets is explicitly provided -> use it (caller-controlled)
      // - if imageTargets is DISABLE_IMAGE_TARGETS ([]) -> disable
      // - otherwise infer from sceneHtml to make AR templates work in self-hosted builds
      if (imageTargets === DISABLE_IMAGE_TARGETS) {
        console.log('🛑 Image targets disabled (DISABLE_IMAGE_TARGETS)')
      } else if (Array.isArray(imageTargets) && imageTargets.length) {
        await configureXR8ImageTargets(imageTargets, 'prop')
      } else {
        const inferred = inferImageTargetsFromSceneHtml(sceneHtml)
        if (inferred.length) {
          // Inferred targets implies an AR image-target scene: disable world tracking for best reliability
          await configureXR8ImageTargets(inferred, 'inferred-from-sceneHtml', { disableWorldTracking: true })
        }
      }
      if (components) {
        registerComponents(components)
      }
      if (primitives) {
        registerPrimitives(primitives)
      }



      const html = document.getElementsByTagName('html')[0]
      const origHtmlClass = html.className
      document.body.insertAdjacentHTML('beforeend', sceneHtml)

      // Logica di tracking AR per mostrare/nascondere lo scan visivo (sostituisce vecchi script inline in model-config.js)
      try {
        const ascene = document.getElementsByTagName('a-scene')[0]
        if (ascene) {
          // Pulisci il Set all'avvio della scena
          activeXRTargets.clear();

          const onFound = (ev: any) => {
            const targetName = ev?.detail?.name;
            if (targetName) {
              console.log('🎯 xrimagefound', targetName);
              activeXRTargets.add(targetName);
              if (activeXRTargets.size > 0) {
                hideScanElements();
              }
            }
          }

          const onLost = (ev: any) => {
            const targetName = ev?.detail?.name;
            if (targetName) {
              console.log('🎯 xrimagelost', targetName);
              activeXRTargets.delete(targetName);
              if (activeXRTargets.size === 0) {
                showScanElements();
              }
            }
          }

          ascene.addEventListener('xrimagefound', onFound as any)
          ascene.addEventListener('xrimagelost', onLost as any)
        }
      } catch (e) {
        console.warn('⚠️ Impossibile agganciare listeners xrimagefound/xrimagelost alla scena', e)
      }

      // Cleanup
      return () => {
        const ascene = document.getElementsByTagName('a-scene')[0]
        if (ascene && ascene.parentNode) {
          ascene.parentNode.removeChild(ascene)
        }
        html.className = origHtmlClass
      }
    }

    // Fire and forget; cleanup is handled by returned function inside run()
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    return run()
  }, [])

  // Avoid JSX to not require react/jsx-runtime
  return React.createElement(React.Fragment, null)
}

const DISABLE_IMAGE_TARGETS: string[] = []

export { AFrameScene, DISABLE_IMAGE_TARGETS }
